import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { NEUTRAL } from "@/theme/tokens";
import { SETTINGS_PANEL_MAX_WIDTH } from "./consts";

export type ScheduleExportTabLayoutProps = {
	/** The tab's controls: its range plus its own options. The left column once there is a preview. */
	settings: ReactNode;
	/** Rendered only when `showPreview` — the minimized card has no room for it. */
	preview: ReactNode;
	/** The download control, pinned below both columns and to the right. */
	actions: ReactNode;
	/**
	 * Document-level commands that sit opposite the download — "reset to default", "save as department
	 * default". Optional: a tab with none keeps the download right-aligned exactly as before.
	 */
	secondaryActions?: ReactNode;
	/** False in the minimized card, which collapses to a single column of controls. */
	showPreview: boolean;
};

/**
 * The shared frame for both export tabs: settings on the left, preview on the right, actions beneath.
 *
 * Mirrors the Daily Printout modal, which is the layout ops already knows — and lives here rather than
 * in each tab because the two tabs differ only in which controls and which preview they hand over.
 */
export const ScheduleExportTabLayout: FC<ScheduleExportTabLayoutProps> = ({ settings, preview, actions, secondaryActions, showPreview }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
		<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: showPreview ? { xs: "column", sm: "row" } : "column", gap: 2 }}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 2,
					alignItems: "stretch",
					p: 2,
					// The same bordered panel the previews use, so the two columns read as one surface split
					// in two rather than as controls floating beside a framed sheet. The faint tint is what
					// keeps the preview the brighter half — the thing the user is actually judging.
					border: `1px solid ${NEUTRAL[300]}`,
					borderRadius: 1,
					bgcolor: NEUTRAL[50],
					// Sized to its own widest control rather than to a guessed number, so the date pickers stay on
					// one row whatever they measure and the preview keeps every pixel the controls do not need.
					// They scroll on their own, so a long list can never push the download button off the card.
					// Full width once the columns stack.
					...(showPreview
						? {
								width: { xs: "100%", sm: "fit-content" },
								maxWidth: { xs: "100%", sm: SETTINGS_PANEL_MAX_WIDTH },
								flexShrink: 0,
								overflowY: "auto",
								// Reserve the scrollbar's space up front: letting it appear later would eat into a
								// fit-content column and wrap the date pickers, which is the whole thing this avoids.
								scrollbarGutter: "stable",
							}
						: {}),
				}}
			>
				{settings}
			</Box>
			{showPreview && preview}
		</Box>

		{/* Ruled off from the panels above so the download reads as the end of the flow, not a stray button. */}
		<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, pt: 2, borderTop: `1px solid ${NEUTRAL[200]}` }}>
			{/* Rendered even when empty: `space-between` needs two children to keep the download pinned
			    right for a tab that has no secondary commands. */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{secondaryActions}</Box>
			{/* Annie walkthrough anchor on the Download/Print pair alone, not the whole footer row: the row also
			    holds the reset/save-as-default commands, and spotlighting those alongside copy that only mentions
			    Download and Print lights up 1420px of dialog to point at two buttons. Both export tabs render
			    through this layout and only one is mounted at a time, so the name stays on one element. */}
			<Box data-annie-target="schedule-export-actions" sx={{ display: "flex", alignItems: "center" }}>
				{actions}
			</Box>
		</Box>
	</Box>
);
